import { CountData, useCountData } from "@/api/instance";
import { MenuItem, MenuItemProps } from "@/components/ui/menuitem.tsx";
import { useAdditionalFilters } from "@/context/additional-filters/AdditionalFiltersContext";
import { useAuth } from "@/context/auth/AuthContext";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Separator } from "../ui/separator";
import ArtworkSetsMenuSection from "./ArtworkSetsMenuSection";

const navItems: ({
  name: string;
  key: keyof CountData;
} & Omit<MenuItemProps, "number">)[] = [
  { to: "/dashboard/artwork", name: "dashboardTitleArtworks", key: "artwork" },
  {
    to: "/dashboard/multipleprint",
    name: "dashboardTitleMultipleprints",
    key: "multipleprint",
  },
  {
    to: "/dashboard/exhibition",
    name: "dashboardTitleExhibitions",
    key: "exhibition",
  },
  {
    to: "/dashboard/bibliography",
    name: "dashboardTitleBibliographies",
    key: "bibliography",
  },
  { to: "/dashboard/auction", name: "dashboardTitleAuctions", key: "auction" },
  { to: "/dashboard/author", name: "dashboardTitleArtistes", key: "author" },
  {
    to: "/dashboard/collector",
    name: "dashboardTitleCollectors",
    key: "collector",
  },
  {
    to: "/dashboard/audiovisual",
    name: "dashboardTitleAudiovisuels",
    key: "audiovisual",
  },
  {
    to: "/dashboard/document",
    name: "dashboardTitleDocuments",
    key: "document",
  },
  { to: "/dashboard/writing", name: "dashboardTitleWrittings", key: "writing" },
  {
    to: "/dashboard/photography",
    name: "dashboardTitlePhotos",
    key: "photography",
  },
];

const TabMenu = ({ children }: { children: React.ReactNode }) => {
  const { instance } = useAuth();
  const { data } = useCountData();
  const location = useLocation();
  const { t } = useTranslation();

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const filters = useAdditionalFilters();

  const totalCount = useMemo(() => {
    return Object.values(data || {}).reduce((acc, curr) => acc + curr, 0);
  }, [data]);

  return (
    <div className="grid grid-cols-[280px_1fr] gap-2">
      <div className="border-r border-slate-200 min-h-screen">
        <div className="p-5 border-b border-slate-200 ">
          <p className="text-start text-[#0c111d] text-xl font-semibold font-['Instrument Sans'] leading-normal">
            {instance}
          </p>
        </div>
        <nav className="w-full">
          {navItems.map(({ key, name, ...props }) => (
            <MenuItem
              key={key}
              number={data?.[key] || 0}
              name={t(name)}
              onClick={(e) => {
                if (isActive(`/dashboard/${key}`)) {
                  filters?.setSetId(undefined);
                  e.preventDefault();
                }
              }}
              {...props}
            />
          ))}
          <p className="text-start text-[10px] px-6 pt-3 ml-2 text-slate-500">
            {totalCount} {t("leftMenuItemsInDatabase")}
          </p>
          {isActive("/dashboard/artwork") && (
            <>
              <Separator className="my-4" />

              <ArtworkSetsMenuSection
                title={t("sharedEnsemble")}
                visibility="shared"
              />

              <Separator className="my-4" />

              <ArtworkSetsMenuSection
                title={t("privateEnsemble")}
                visibility="private"
              />
            </>
          )}
        </nav>
      </div>
      <div className="py-5 px-6 w-full">{children}</div>
    </div>
  );
};

export default TabMenu;
